import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQueryClient } from "@tanstack/react-query";
import { Toast } from "shared-ui/src/components/Toast";
import {
  ProductDetailDto,
  ProductDetailDrawerRequestDto,
  ShoppingCartDto,
  ShoppingCartLineDto,
} from "@lib/ShiOneClient";
import useGetShoppingCartLines from "@features/ecommerce/cart/useGetShoppingCartLines";
import { computeCartLineTotal } from "@features/ecommerce/cart/cartTotals";
import useGetCartProductThumbnails from "@features/unified-user-header-feature/hooks/useGetCartProductThumbnails";
import useGetCartProductAvailability from "@features/unified-user-header-feature/hooks/useGetCartProductAvailability";
import { QuoteProductDetailDrawer } from "./QuoteProductDetailDrawer";
import { getCreateQuoteErrorMessage } from "./createQuoteError";
import { SHOPPING_CART_QUERY_KEY } from "@features/unified-user-header-feature/utils/HeaderQueryKeys";
import { STEPS } from "./composer/types";
import useQuoteComposerState, {
  type SeedLine,
} from "./composer/useQuoteComposerState";
import useStorefrontCurrency from "@features/ecommerce/hooks/useStorefrontCurrency";
import {
  ProductLinesTable,
  QuoteDetailsStep,
  ShippingStep,
  SummaryPanel,
  OtherStorefrontPricingNotice,
  VerticalStepper,
} from "./composer/steps";

const STEP_DESCRIPTIONS = [
  "These products come from your cart. Adjust quantities before saving them to the quote.",
  "Select a shipping address to view shipping methods, freight charges, and estimated taxes for the quote.",
  "Name your quote and optionally email it when created.",
];

/** Two-decimal rounding matching the server's per-unit bundle price (MidpointRounding.AwayFromZero). */
const roundToCents = (value: number) =>
  Math.round((value + Number.EPSILON) * 100) / 100;

/** Product class of a saved-quote cart line; the cart reports it as "SHIQuoteItem" in any casing. */
const QUOTE_ITEM_PRODUCT_CLASS = "shiquoteitem";

/**
 * Map a cart line into a composer seed line; the cart's itemId is the product id used on create.
 * The mini-cart line only carries a configured bundle's base price, so bundles take their per-unit
 * price (base plus options) from the enriched cart line, matching the cart page and the saved quote.
 * Saved-quote (SHIQuoteItem) lines reference their cart line so the server prices, weighs, and taxes
 * them from the quote; that is the only way a fabricated quote line (no product id) can be saved.
 * A line whose quote has expired no longer holds a price and is left out (null), as on shi.com, whether
 * or not it names a catalog product.
 * Only bundle and saved-quote rows reference their cart line, so commodity-only quotes never trigger a
 * server cart read.
 */
function cartLineToSeed(
  item: ShoppingCartDto,
  bundleLinesByNumber: Map<number, ShoppingCartLineDto>,
  quoteLinesByNumber: Map<number, ShoppingCartLineDto>,
): SeedLine | null {
  const productId = item.itemId;
  const quantity = item.quantity && item.quantity > 0 ? item.quantity : 1;
  const quoteLine =
    item.lineNumber != null
      ? quoteLinesByNumber.get(item.lineNumber)
      : undefined;
  if (quoteLine?.isQuoteExpired) return null;
  if (quoteLine) {
    const quotedProductId =
      !quoteLine.isFabricatedQuoteItem && productId != null && productId > 0
        ? productId
        : 0;
    return {
      line: {
        productId: quotedProductId,
        productClass: QUOTE_ITEM_PRODUCT_CLASS,
        productName:
          item.productName ??
          (quotedProductId > 0 ? `Product ${quotedProductId}` : "Quoted item"),
        shiPartNumber:
          quotedProductId > 0 ? String(quotedProductId) : undefined,
        unitPrice: item.unitPrice,
        sourceCartLineNumber: item.lineNumber,
        sourceQuoteItemDescriptor: quoteLine.productIdentity,
      },
      quantity,
    };
  }
  if (productId == null || productId <= 0) return null;
  const bundleLine =
    item.lineNumber != null
      ? bundleLinesByNumber.get(item.lineNumber)
      : undefined;
  const unitPrice = bundleLine
    ? roundToCents(
        computeCartLineTotal(bundleLine) / (bundleLine.quantity || 1),
      )
    : item.unitPrice;
  return {
    line: {
      productId,
      productClass: "shicommodity",
      productName: item.productName ?? `Product ${productId}`,
      shiPartNumber: String(productId),
      unitPrice,
      sourceCartLineNumber: bundleLine ? item.lineNumber : undefined,
    },
    quantity,
  };
}

export interface SaveQuoteDialogProps {
  open: boolean;
  onClose: () => void;
  /** Cart lines to seed the quote with (the mini-cart's flat line list). */
  cartItems: ShoppingCartDto[] | undefined;
  /** Called with the new quote id after a successful create. */
  onCreated?: (quoteId: number) => void;
  /** Reports whether the user has typed anything that closing would discard. */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * SPU-493/494 — Cart-fed Save Quote dialog. Seeds the shared quote composer with the current
 * cart lines and presents the Products / Shipping & tax / Quote details flow as scroll-spy
 * stacked sections inside a dialog, matching the raven/quotes prototype. The
 * catalog-search variant of the same flow is CreateQuotePage; both share composer/ components.
 */
export default function SaveQuoteDialog({
  open,
  onClose,
  cartItems,
  onCreated,
  onDirtyChange,
}: SaveQuoteDialogProps) {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const composer = useQuoteComposerState({ allowZeroQuantity: true });
  const {
    storefronts,
    storefrontsLoading,
    selectedOrgUnitId,
    shippingOrgUnitId,
    isOtherStorefrontSelected,
    createQuotesEnabled,
    lines,
    setQuantity,
    seedLines,
    resetComposer,
    name,
    setName,
    description,
    setDescription,
    selectedShippingAddressId,
    selectedShippingMethodId,
    setSelectedShippingMethodId,
    includeShipping,
    handleIncludeShippingChange,
    handleSelectShippingAddress,
    handleStorefrontChange,
    shippingMethodsMutation,
    includeTax,
    handleToggleTax,
    taxMutation,
    taxAmount,
    retryTax,
    userEmail,
    sendCopyToSelf,
    setSendCopyToSelf,
    sendToOthers,
    setSendToOthers,
    emailRecipients,
    setEmailRecipients,
    emailMessage,
    setEmailMessage,
    subtotal,
    totalQuantity,
    activeLineCount,
    selectedFreight,
    shippingCalculating,
    taxCalculating,
    canCreate,
    createMutation,
    handleCreate,
  } = composer;
  const { currency: storefrontCurrency } = useStorefrontCurrency();
  const effectiveCurrencyId = composer.currencyId ?? storefrontCurrency.id;

  const isPending = createMutation.isPending;

  // Seeded lines come from the cart and survive a close, so only typed input counts as dirty.
  const isDirty =
    !createMutation.isSuccess &&
    (name.trim() !== "" ||
      description.trim() !== "" ||
      emailRecipients.length > 0 ||
      emailMessage.trim() !== "");
  useEffect(() => {
    onDirtyChange?.(open && isDirty);
  }, [open, isDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const [activeStep, setActiveStep] = useState(0);
  const [nameError, setNameError] = useState(false);
  // Cart lines from expired quotes, left out at seeding (an expired quote no longer holds a price, as on
  // shi.com) and listed so the user knows why they are missing.
  const [expiredQuoteLines, setExpiredQuoteLines] = useState<string[]>([]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([null, null, null]);

  // isFetching (not isInitialLoading) so a stale cached cart is refetched before seeding bundle prices.
  const {
    data: cartLines,
    isFetching: cartLinesFetching,
    isError: cartLinesError,
    refetch: refetchCartLines,
  } = useGetShoppingCartLines(open);
  const seededForOpenRef = useRef(false);

  // Seed lines from the cart each time the dialog opens (fresh snapshot of the current cart), once the
  // enriched cart lines needed for bundle pricing have settled.
  useEffect(() => {
    if (!open) {
      // The component stays mounted while `open` toggles, so clear prior inputs on close; otherwise
      // reopening would preserve a stale name/ship-to/email. Clearing here rather than when seeding keeps
      // anything typed while the cart is still loading.
      seededForOpenRef.current = false;
      resetComposer();
      setExpiredQuoteLines([]);
      return;
    }
    // A failed read must not seed from the mini-cart alone: fabricated quote lines would be dropped and
    // quoted catalog lines would be repriced from the catalog on save.
    if (seededForOpenRef.current || cartLinesFetching || cartLinesError) return;
    seededForOpenRef.current = true;
    const bundleLinesByNumber = new Map<number, ShoppingCartLineDto>();
    const quoteLinesByNumber = new Map<number, ShoppingCartLineDto>();
    for (const line of cartLines ?? []) {
      if (line.lineNumber == null) continue;
      if ((line.configId ?? 0) > 0)
        bundleLinesByNumber.set(line.lineNumber, line);
      if (line.origShiSystemId?.toLowerCase() === QUOTE_ITEM_PRODUCT_CLASS)
        quoteLinesByNumber.set(line.lineNumber, line);
    }
    const expired = (cartItems ?? [])
      .filter(
        (item) =>
          item.lineNumber != null &&
          quoteLinesByNumber.get(item.lineNumber)?.isQuoteExpired,
      )
      .map((item) => item.productName || "Quoted item");
    setExpiredQuoteLines(expired);
    const seeds = (cartItems ?? [])
      .map((item) =>
        cartLineToSeed(item, bundleLinesByNumber, quoteLinesByNumber),
      )
      .filter((s): s is SeedLine => s !== null);
    seedLines(seeds);
    setActiveStep(0);
    setNameError(false);
    setTimeout(() => scrollRef.current?.scrollTo?.({ top: 0 }), 0);
    // Intentionally keyed on open/settle only: re-seeding on every cart refetch would clobber edits.
  }, [open, cartLinesFetching, cartLinesError]);

  // ShoppingCartDto carries no image, so thumbnails come from the same cart endpoint the mini-cart
  // and the mini-cart use (product id === cart itemId here). Looked up at render time by product id.
  const itemIds = useMemo(
    () =>
      (cartItems ?? [])
        .map((i) => i.itemId)
        .filter((id): id is number => id != null && id > 0),
    [cartItems],
  );
  const { data: thumbnails } = useGetCartProductThumbnails(itemIds, open);

  const imagesByProductId = useMemo(() => {
    const map: Record<number, string> = {};
    for (const [key, url] of Object.entries(thumbnails ?? {})) {
      const id = Number(key);
      if (Number.isFinite(id) && url) map[id] = url;
    }
    return map;
  }, [thumbnails]);

  // Per-item stock availability from the cart endpoint, keyed by product id === itemId.
  // Prefer the catalog/PnA `itemStatus` string so the chip matches the product detail drawer and the
  // saved quote (both surface that same free-text status). Only fall back to the coarse `inStock`
  // boolean when no status string is present; `inStock` is `TotalUnits > 0`, which flags a
  // catalog-"In Stock" item with zero warehouse units as "Backorder". Unknown/unfetched items are
  // omitted so the row shows no chip rather than mislabeling unknown stock.
  const { data: availabilityList } = useGetCartProductAvailability(
    itemIds,
    open,
  );

  const availabilityByProductId = useMemo(() => {
    const map: Record<number, string> = {};
    for (const entry of availabilityList ?? []) {
      if (entry.itemId == null) continue;
      if (entry.itemStatus) {
        map[entry.itemId] = entry.itemStatus;
      } else if (entry.inStock != null) {
        map[entry.itemId] = entry.inStock ? "In Stock" : "Backorder";
      }
    }
    return map;
  }, [availabilityList]);

  // Cart lines carry no mfr part number, but the cart availability endpoint returns it from the
  // catalog product summary. Map it by product id so rows show the real "Mfr #" instead of the SHI #.
  const mfrPartByProductId = useMemo(() => {
    const map: Record<number, string> = {};
    for (const entry of availabilityList ?? []) {
      if (entry.itemId == null) continue;
      if (entry.mfrPartNumber) map[entry.itemId] = entry.mfrPartNumber;
    }
    return map;
  }, [availabilityList]);

  // Product Details Drawer, opened from a product name in Step 1 (reuses the quote-permissioned
  // catalog enrichment endpoint, same as ViewQuotesPage / QuoteDetailPage).
  const [drawerProduct, setDrawerProduct] = useState<ProductDetailDto | null>(
    null,
  );
  const [drawerRequest, setDrawerRequest] =
    useState<ProductDetailDrawerRequestDto | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const openProductDrawer = useCallback(
    (productId: number) => {
      const orgUnitId = Number(selectedOrgUnitId);
      if (!Number.isFinite(orgUnitId) || orgUnitId <= 0) return;
      const product = lines.find(
        (l) => l.product.productId === productId,
      )?.product;
      if (!product) return;
      const descriptor = String(productId);
      // A quoted catalog product is described by the catalog, not by its saved-quote cart class.
      const productClass =
        !product.productClass ||
        product.productClass === QUOTE_ITEM_PRODUCT_CLASS
          ? "shicommodity"
          : product.productClass;
      setDrawerProduct(
        new ProductDetailDto({
          productName: product.productName,
          mfrPartNum: product.manufacturerPartNumber,
          productDescriptor: descriptor,
          productClass,
          price: product.unitPrice,
          imageUrl: imagesByProductId[productId] ?? product.imageUrl,
          cNetProductId: undefined,
        }),
      );
      setDrawerRequest(
        new ProductDetailDrawerRequestDto({
          productDescriptor: descriptor,
          productClass,
          orgunitId: orgUnitId,
          cNetProductId: undefined,
        }),
      );
      setDrawerOpen(true);
    },
    [lines, imagesByProductId, selectedOrgUnitId],
  );

  const closeProductDrawer = useCallback(() => {
    setDrawerOpen(false);
    setDrawerProduct(null);
    setDrawerRequest(null);
  }, []);

  // Drawer state is independent of the dialog's Modal, so reset it whenever the host dialog closes
  // to avoid the drawer persisting or re-opening across close/reopen cycles.
  useEffect(() => {
    if (!open) closeProductDrawer();
  }, [open, closeProductDrawer]);

  const handleScrollSpy = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const scrollTop = container.scrollTop;
    // When scrolled to (near) the bottom, always mark the last section active. A short final
    // section can't be scrolled to the top, so offset-based detection alone never selects it.
    if (scrollTop + container.clientHeight >= container.scrollHeight - 4) {
      setActiveStep(sectionRefs.current.length - 1);
      return;
    }
    let active = 0;
    sectionRefs.current.forEach((el, i) => {
      if (el && el.offsetTop <= scrollTop + 40) active = i;
    });
    setActiveStep(active);
  }, []);

  const scrollCallbackRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (scrollRef.current)
        scrollRef.current.removeEventListener("scroll", handleScrollSpy);
      scrollRef.current = el;
      if (el) {
        el.addEventListener("scroll", handleScrollSpy, { passive: true });
        handleScrollSpy();
      }
    },
    [handleScrollSpy],
  );

  const scrollToSection = useCallback((i: number) => {
    const el = sectionRefs.current[i];
    if (!scrollRef.current || !el) return;
    scrollRef.current.scrollTo?.({ top: el.offsetTop, behavior: "smooth" });
  }, []);

  const onSave = () => {
    if (!name.trim()) {
      setNameError(true);
      scrollToSection(2);
      return;
    }
    handleCreate((quoteId) => {
      queryClient.invalidateQueries({ queryKey: [SHOPPING_CART_QUERY_KEY] });
      Toast("Quote saved successfully.");
      onCreated?.(quoteId);
      onClose();
    });
  };

  const sectionHeader = (index: number) => (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        px: 3,
        pt: 2.5,
        pb: 1,
        bgcolor: "background.default",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography variant="h6" fontWeight={500}>
        {STEPS[index]}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {STEP_DESCRIPTIONS[index]}
      </Typography>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: "90vh" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "background.paper",
            pr: 1,
          }}
        >
          <span>Save cart as quote</span>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {!storefrontsLoading && storefronts && storefronts.length > 1 && (
              <TextField
                select
                label="Storefront"
                value={selectedOrgUnitId}
                onChange={(e) => handleStorefrontChange(Number(e.target.value))}
                size="small"
                sx={{ minWidth: 220 }}
              >
                {storefronts.map((s) => (
                  <MenuItem key={s.orgUnitId} value={s.orgUnitId!}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <IconButton
              aria-label="close"
              onClick={onClose}
              disabled={isPending}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ p: 0, display: "flex", flexDirection: "column", minHeight: 0 }}
        >
          {createMutation.isError && (
            <Alert severity="error" sx={{ m: 2 }}>
              {getCreateQuoteErrorMessage(
                createMutation.error,
                "Sorry, we couldn't save the quote. Please try again.",
              )}
            </Alert>
          )}
          {isOtherStorefrontSelected && <OtherStorefrontPricingNotice sx={{ m: 2 }} />}

          <Divider />

          <Box
            sx={{
              display: "flex",
              alignItems: "stretch",
              flex: 1,
              minHeight: 0,
            }}
          >
            <VerticalStepper
              steps={STEPS}
              activeStep={activeStep}
              onSelect={scrollToSection}
            />
            <Divider orientation="vertical" flexItem />

            {/* Scroll-spy container: the three steps stacked with sticky headers. */}
            <Box
              ref={scrollCallbackRef}
              sx={{
                flex: 1,
                minWidth: 0,
                overflowY: "auto",
                bgcolor: "background.default",
              }}
            >
              <Box
                ref={(el: HTMLDivElement | null) => {
                  sectionRefs.current[0] = el;
                }}
              >
                {sectionHeader(0)}
                <Box sx={{ px: 3, py: 2 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Due to ongoing global component shortages affecting memory,
                    storage, and other critical hardware, OEMs have implemented
                    updated policies allowing for price adjustments up until the
                    time of shipment. Accordingly, quoted prices and lead times
                    are subject to change prior to shipment. We remain committed
                    to keeping you informed of any changes and will communicate
                    promptly as updates occur.
                  </Alert>
                  {cartLinesError && !cartLinesFetching && (
                    <Alert
                      severity="error"
                      sx={{ mb: 2 }}
                      data-testid="cart-lines-error"
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          onClick={() => void refetchCartLines()}
                        >
                          Retry
                        </Button>
                      }
                    >
                      Your cart could not be loaded, so its items cannot be
                      quoted yet.
                    </Alert>
                  )}
                  {expiredQuoteLines.length > 0 && (
                    <Alert
                      severity="warning"
                      sx={{ mb: 2 }}
                      data-testid="expired-quote-lines"
                    >
                      Some items in your cart come from quotes that have expired
                      and were left out of this quote:{" "}
                      {expiredQuoteLines.join(", ")}.
                    </Alert>
                  )}
                  <ProductLinesTable
                    lines={lines}
                    currencyId={effectiveCurrencyId}
                    onQtyChange={setQuantity}
                    onNameClick={
                      Number(selectedOrgUnitId) > 0
                        ? openProductDrawer
                        : undefined
                    }
                    minQty={0}
                    imagesByProductId={imagesByProductId}
                    availabilityByProductId={availabilityByProductId}
                    mfrPartByProductId={mfrPartByProductId}
                    // Cart-fed, so the user can read cart availability: an unchecked line can be checked.
                    canVerifyAvailability
                    emptyText="Your cart is empty. Add products to your cart to save them as a quote."
                  />
                </Box>
              </Box>

              <Box
                ref={(el: HTMLDivElement | null) => {
                  sectionRefs.current[1] = el;
                }}
              >
                {sectionHeader(1)}
                <Box sx={{ px: 3, py: 2 }}>
                  <ShippingStep
                    createQuotesEnabled={createQuotesEnabled}
                    currencyId={effectiveCurrencyId}
                    shippingOrgUnitId={shippingOrgUnitId}
                    selectedShippingAddressId={selectedShippingAddressId}
                    onSelectAddress={handleSelectShippingAddress}
                    shippingMethods={shippingMethodsMutation.data ?? []}
                    shippingCheckPending={Boolean(
                      shippingMethodsMutation.isPending,
                    )}
                    shippingCheckError={Boolean(
                      shippingMethodsMutation.isError,
                    )}
                    hasCheckedShipping={Boolean(
                      shippingMethodsMutation.isSuccess,
                    )}
                    hasLines={activeLineCount > 0}
                    selectedShippingMethodId={selectedShippingMethodId}
                    onSelectMethod={setSelectedShippingMethodId}
                    includeTax={includeTax}
                    onToggleTax={handleToggleTax}
                    taxPending={Boolean(taxMutation.isPending)}
                    taxError={Boolean(taxMutation.isError)}
                    onRetryTax={retryTax}
                    taxAmount={taxAmount}
                    includeShipping={includeShipping}
                    onIncludeShippingChange={handleIncludeShippingChange}
                  />
                </Box>
              </Box>

              <Box
                ref={(el: HTMLDivElement | null) => {
                  sectionRefs.current[2] = el;
                }}
                sx={{ minHeight: "calc(90vh - 220px)" }}
              >
                {sectionHeader(2)}
                <Box sx={{ px: 3, py: 2 }}>
                  <QuoteDetailsStep
                    name={name}
                    setName={(v) => {
                      setName(v);
                      if (v.trim()) setNameError(false);
                    }}
                    description={description}
                    setDescription={setDescription}
                    userEmail={userEmail}
                    sendCopyToSelf={sendCopyToSelf}
                    setSendCopyToSelf={setSendCopyToSelf}
                    sendToOthers={sendToOthers}
                    setSendToOthers={setSendToOthers}
                    emailRecipients={emailRecipients}
                    setEmailRecipients={setEmailRecipients}
                    emailMessage={emailMessage}
                    setEmailMessage={setEmailMessage}
                    disabled={isPending}
                    nameError={nameError}
                  />
                </Box>
              </Box>
            </Box>

            <Divider orientation="vertical" flexItem />

            <SummaryPanel
              productCount={activeLineCount}
              currencyId={effectiveCurrencyId}
              totalQuantity={totalQuantity}
              subtotal={subtotal}
              shippingCost={selectedFreight}
              taxAmount={taxAmount}
              shippingPending={shippingCalculating}
              taxPending={taxCalculating}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            justifyContent: "space-between",
            bgcolor: "background.paper",
          }}
        >
          <Button onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!canCreate}
            startIcon={isPending ? <CircularProgress size={16} /> : undefined}
          >
            {isPending ? "Saving…" : "Save as Quote"}
          </Button>
        </DialogActions>
      </Dialog>

      <QuoteProductDetailDrawer
        open={drawerOpen}
        product={drawerProduct}
        request={drawerRequest}
        currencyId={effectiveCurrencyId}
        onClose={closeProductDrawer}
        zIndex={theme.zIndex.modal + 1}
      />
    </>
  );
}
